import { User } from '../models/User';

// Helper to get function URL from CRA environment variables
const getFunctionUrl = (): string => {
  return process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
};

export class AdminService {
  constructor() {
    // All admin operations go through Azure Functions via fetch
  }

  /**
   * Ensure admin entry exists in Admins table (create if not exists with isAdmin: false)
   * Uses backend API to avoid requiring direct table access permissions
   */
  async ensureAdminEntry(userObjectId: string, userEmail: string): Promise<void> {
    try {
      if (!userObjectId && !userEmail) {
        console.log('No userObjectId or userEmail provided for ensureAdminEntry');
        return;
      }

      const functionUrl = getFunctionUrl();
      const endpoint = `${functionUrl}/ensure_admin_entry`;
      
      console.log('Calling ensure_admin_entry:', { userObjectId, userEmail, endpoint });
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId,
          userEmail
        })
      });

      console.log('ensure_admin_entry response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || `HTTP ${response.status}` };
        }
        console.error('ensure_admin_entry error:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Admin entry ${data.isNew ? 'created' : 'updated'}:`, data.rowKey);
      console.log('Full response:', data);
    } catch (error: any) {
      console.error('❌ Error ensuring admin entry:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        userObjectId,
        userEmail,
        endpoint: `${getFunctionUrl()}/ensure_admin_entry`
      });
      
      // Log to help with debugging - but don't throw to allow login to continue
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        console.error('⚠️ Function endpoint not found. Is ensure_admin_entry deployed?');
      } else if (error.message?.includes('500') || error.message?.includes('Internal server error')) {
        console.error('⚠️ Server error. Check Azure Function logs for details.');
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        console.error('⚠️ Network error. Check function URL:', getFunctionUrl());
      }
      
      // Don't throw - allow login to continue even if admin entry creation fails
    }
  }

  async isAdmin(userObjectId: string, userEmail: string): Promise<boolean> {
    try {
      console.log('Checking admin status for:', { userObjectId, userEmail });
      
      if (!userObjectId && !userEmail) {
        console.log('No userObjectId or userEmail provided');
        return false;
      }

      const functionUrl = getFunctionUrl();
      const endpoint = `${functionUrl}/check_admin_status`;
      
      console.log('Calling check_admin_status:', { userObjectId, userEmail, endpoint });
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId,
          userEmail
        })
      });

      console.log('check_admin_status response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || `HTTP ${response.status}` };
        }
        console.error('check_admin_status error:', errorData);
        // On error, default to false (non-admin)
        return false;
      }

      const data = await response.json();
      const isAdminStatus = data.isAdmin === true;
      console.log(`Admin status: ${isAdminStatus}`, { rowKey: data.rowKey });
      return isAdminStatus;
    } catch (error: any) {
      console.error('Error checking admin status:', error);
      // On error, default to false (non-admin)
      return false;
    }
  }

  /**
   * Get admin credentials for API calls
   */
  private async getAdminCredentials(): Promise<{ userObjectId: string; userEmail: string }> {
    // Get from MSAL account info (stored in session)
    const accountStr = sessionStorage.getItem('msalAccount');
    if (accountStr) {
      const account = JSON.parse(accountStr);
      return {
        userObjectId: account.localAccountId || account.homeAccountId || '',
        userEmail: account.username || ''
      };
    }
    return { userObjectId: '', userEmail: '' };
  }

  /**
   * List all registered users
   */
  async listUsers(): Promise<User[]> {
    try {
      const credentials = await this.getAdminCredentials();
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(`${functionUrl}/list_users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId: credentials.userObjectId,
          userEmail: credentials.userEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to list users' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (error: any) {
      console.error('List users error:', error);
      throw error;
    }
  }

  /**
   * Update user status (enable/disable)
   */
  async updateUserStatus(userId: string, status: 'active' | 'disabled'): Promise<void> {
    try {
      const credentials = await this.getAdminCredentials();
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(`${functionUrl}/update_user_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId: credentials.userObjectId,
          userEmail: credentials.userEmail,
          userId,
          status
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update user status' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Update user status error:', error);
      throw error;
    }
  }

  /**
   * Assign container to user
   */
  async assignUserContainer(userId: string, containerName: string, permissions: 'read' | 'write' | 'readwrite'): Promise<void> {
    try {
      const credentials = await this.getAdminCredentials();
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(`${functionUrl}/assign_user_container`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId: credentials.userObjectId,
          userEmail: credentials.userEmail,
          userId,
          containerName,
          permissions
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to assign container' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Assign container error:', error);
      throw error;
    }
  }

  /**
   * Remove container assignment from user
   */
  async removeUserContainer(userId: string, containerName: string): Promise<void> {
    try {
      const credentials = await this.getAdminCredentials();
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(`${functionUrl}/remove_user_container`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId: credentials.userObjectId,
          userEmail: credentials.userEmail,
          userId,
          containerName
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to remove container' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Remove container error:', error);
      throw error;
    }
  }

  /**
   * List all admin users (for admin management)
   */
  async listAdmins(): Promise<any[]> {
    try {
      const credentials = await this.getAdminCredentials();
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(`${functionUrl}/list_admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId: credentials.userObjectId,
          userEmail: credentials.userEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to list admins' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.admins || [];
    } catch (error: any) {
      console.error('List admins error:', error);
      throw error;
    }
  }

  /**
   * Update admin status (grant/revoke admin access)
   */
  async updateAdminStatus(adminRowKey: string, isAdmin: boolean): Promise<void> {
    try {
      const credentials = await this.getAdminCredentials();
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(`${functionUrl}/update_admin_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userObjectId: credentials.userObjectId,
          userEmail: credentials.userEmail,
          adminRowKey,
          isAdmin
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update admin status' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Update admin status error:', error);
      throw error;
    }
  }
}
